import type {
  TransitionPresentation,
  TransitionPresentationComponentProps,
} from "@remotion/transitions";
import type React from "react";
import { AbsoluteFill, Easing, interpolate } from "remotion";
import { lensTransforms, StackedBlur } from "./stacked-blur.tsx";
import { isTransitionIdle } from "./transition-idle.ts";
import { useStackedBlur, useTransitionBlur } from "./use-transition-blur.ts";

const clampOpts = {
  extrapolateLeft: "clamp" as const,
  extrapolateRight: "clamp" as const,
};

// The ring's copy spacing stays under 2 sigma of the Gaussian, so it reads as one blur.
const LENS_RING = 8;
const LENS_RADIUS = 1.2;
const LENS_GAUSSIAN = 0.5;
const ENTER_OPAQUE = 0.52;

export type FocusPullProps = {
  blur?: number;
  brightness?: number;
};

const FocusPullPresentation: React.FC<
  TransitionPresentationComponentProps<FocusPullProps>
> = ({
  children,
  presentationProgress,
  presentationDirection,
  passedProps,
}) => {
  const { blur: fullBlur = 16, brightness = 1.3 } = passedProps;
  const blur = useTransitionBlur(fullBlur);
  const lens = useStackedBlur();
  const enterBrightness = 1 + (brightness - 1) * (0.25 / 0.3);
  const entering = presentationDirection === "entering";
  const p = presentationProgress;

  if (isTransitionIdle(presentationDirection, p)) {
    return <AbsoluteFill>{children}</AbsoluteFill>;
  }

  const defocus = entering
    ? p >= 0.9
      ? 0
      : interpolate(p, [0.35, 0.9], [blur, 0], {
          ...clampOpts,
          easing: Easing.out(Easing.quad),
        })
    : p <= 0.05
      ? 0
      : interpolate(p, [0.05, 0.55], [0, blur], {
          ...clampOpts,
          easing: Easing.in(Easing.quad),
        });
  const glow = entering
    ? interpolate(p, [0.4, 0.85], [enterBrightness, 1], clampOpts)
    : interpolate(p, [0.1, 0.55], [1, brightness], clampOpts);
  const opacity = entering
    ? interpolate(p, [0.32, ENTER_OPAQUE], [0, 1], clampOpts)
    : 1;
  // Nothing to blur once a scene is invisible or covered by the opaque entering scene.
  const active =
    opacity > 0 && (entering ? p < 0.9 : p > 0.05 && p < ENTER_OPAQUE);

  const style: React.CSSProperties = {
    opacity,
    scale: entering
      ? `${interpolate(p, [0.32, 1], [1.05, 1], {
          ...clampOpts,
          easing: Easing.out(Easing.cubic),
        })}`
      : `${interpolate(p, [0, 0.6], [1, 1.05], {
          ...clampOpts,
          easing: Easing.bezier(0.42, 0, 0.58, 1),
        })}`,
    filter: active
      ? `blur(${defocus * (lens ? LENS_GAUSSIAN : 1)}px) brightness(${glow})`
      : undefined,
  };

  return (
    <AbsoluteFill style={style}>
      {lens && active && defocus > 0 ? (
        <StackedBlur
          transforms={lensTransforms(defocus * LENS_RADIUS, LENS_RING)}
        >
          {children}
        </StackedBlur>
      ) : (
        children
      )}
    </AbsoluteFill>
  );
};

export function focusPull(
  props: FocusPullProps = {},
): TransitionPresentation<FocusPullProps> {
  return {
    component: FocusPullPresentation,
    props,
  };
}
